import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Fade,
  Slide,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Warning as WarningIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Map as MapIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useAlerts } from '../contexts/AlertsContext';

interface EmergencyAlert {
  id: string;
  user_id: string;
  username: string;
  user_group: string;
  latitude: number;
  longitude: number;
  address: string;
  status: 'active' | 'responded' | 'resolved';
  created_at: string;
  updated_at: string;
  notes?: string;
  hp_number?: string;
}



export default function EmergencyAlerts() {
  const [alerts, setAlerts] = useState<EmergencyAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Removed unused user state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<EmergencyAlert | null>(null);
  const [notes, setNotes] = useState('');
  const { fetchAlertsCount, connectionStatus } = useAlerts();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [sortBy, setSortBy] = useState<'created_at' | 'status'>('created_at');
  const [searchQuery, setSearchQuery] = useState('');
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Fetch alerts
      const { data: alertsData, error: alertsError } = await supabase
        .from('emergency_alerts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (alertsError) throw alertsError;
      if (!alertsData) return;

      // 2. Get unique user IDs from alerts
      const userIds = [...new Set(alertsData.map(a => a.user_id).filter(Boolean))];

      if (userIds.length > 0) {
        // 3. Fetch current user info for those IDs
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, user_group, hp_number')
          .in('id', userIds);

        if (usersError) throw usersError;

        // 4. Create a map for easy lookup
        const userMap = new Map(usersData.map(u => [u.id, u]));

        // 5. Combine the data, overriding the stored user_group and adding hp_number
        const combinedAlerts = alertsData.map(alert => ({
          ...alert,
          user_group: userMap.get(alert.user_id)?.user_group || alert.user_group,
          hp_number: userMap.get(alert.user_id)?.hp_number || '',
        }));
        setAlerts(combinedAlerts);
      } else {
        setAlerts(alertsData);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUserAndAlerts = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data, error } = await supabase
            .from('users')
            .select('user_role')
            .eq('id', user.id)
            .single();
          if (error) throw error;
          setUserRole(data?.user_role || null);
          await fetchAlerts();
        } else {
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
        setError('Could not verify user session.');
      }
      setLoading(false);
    };

    fetchUserAndAlerts();

    // Enhanced real-time subscription with immediate updates
    const channel = supabase
      .channel('emergency_alerts_realtime_enhanced')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'emergency_alerts' },
        (payload) => {
          console.log('New alert received:', payload);
          // Immediately update local state for faster UI response
          setAlerts(prevAlerts => {
            const newAlert = payload.new as EmergencyAlert;
            return [newAlert, ...prevAlerts];
          });
          // Update counts immediately
          fetchAlertsCount();
          // Then fetch fresh data to ensure consistency
          fetchAlerts();
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'emergency_alerts' },
        (payload) => {
          console.log('Alert updated:', payload);
          // Immediately update local state
          setAlerts(prevAlerts => {
            const updatedAlert = payload.new as EmergencyAlert;
            return prevAlerts.map(alert => 
              alert.id === updatedAlert.id ? updatedAlert : alert
            );
          });
          // Update counts immediately
          fetchAlertsCount();
          // Then fetch fresh data
          fetchAlerts();
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'emergency_alerts' },
        (payload) => {
          console.log('Alert deleted:', payload);
          // Immediately update local state
          setAlerts(prevAlerts => {
            const deletedAlertId = payload.old.id;
            return prevAlerts.filter(alert => alert.id !== deletedAlertId);
          });
          // Update counts immediately
          fetchAlertsCount();
          // Then fetch fresh data
          fetchAlerts();
        }
      )
      .subscribe((status) => {
        console.log('Real-time subscription status:', status);
        if (status === 'CHANNEL_ERROR') {
          console.error('Real-time channel error, attempting to reconnect...');
          // Attempt to reconnect after a short delay
          setTimeout(() => {
            channel.subscribe();
          }, 2000);
        }
      });

    return () => {
      console.log('Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [fetchAlerts, fetchAlertsCount]);

  const openUpdateDialog = (alert: EmergencyAlert) => {
    setSelectedAlert(alert);
    setNotes(alert.notes || '');
    setDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'error';
      case 'responded':
        return 'warning';
      case 'resolved':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'responded':
        return 'Responded';
      case 'resolved':
        return 'Resolved';
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleRespond = async () => {
    if (!selectedAlert) return;

    try {
      setUpdating(true);
      const { error: updateError } = await supabase
        .from('emergency_alerts')
        .update({
          status: 'responded',
          notes: notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedAlert.id);

      if (updateError) {
        setError(updateError.message);
      } else {
        setRespondDialogOpen(false);
        fetchAlerts();
        fetchAlertsCount();
        setSelectedAlert(null);
        setNotes('');
      }
    } catch (error) {
      setError('Failed to respond to alert');
    } finally {
      setUpdating(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedAlert) return;

    try {
      setUpdating(true);
      const { error: updateError } = await supabase
        .from('emergency_alerts')
        .update({
          status: 'resolved',
          notes: notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedAlert.id);

      if (updateError) {
        setError(updateError.message);
      } else {
        setResolveDialogOpen(false);
        fetchAlerts();
        fetchAlertsCount();
        setSelectedAlert(null);
        setNotes('');
      }
    } catch (error) {
      setError('Failed to resolve alert');
    } finally {
      setUpdating(false);
    }
  };

  const openRespondDialog = (alert: EmergencyAlert) => {
    setSelectedAlert(alert);
    setNotes('');
    setRespondDialogOpen(true);
  };

  const openResolveDialog = (alert: EmergencyAlert) => {
    setSelectedAlert(alert);
    setNotes(alert.notes || '');
    setResolveDialogOpen(true);
  };

  const filteredAlerts = alerts.filter(alert => {
    // Status filter
    let statusMatch = true;
    if (activeTab === 0) statusMatch = alert.status === 'active';
    if (activeTab === 1) statusMatch = alert.status === 'responded';
    if (activeTab === 2) statusMatch = alert.status === 'resolved';
    
    // Search filter
    const searchMatch = searchQuery === '' || 
      alert.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.user_group?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.notes?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      alert.address?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return statusMatch && searchMatch;
  });

  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    if (sortBy === 'created_at') {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    return a.status.localeCompare(b.status);
  });

  const activeCount = alerts.filter(a => a.status === 'active').length;
  const respondedCount = alerts.filter(a => a.status === 'responded').length;
  const resolvedCount = alerts.filter(a => a.status === 'resolved').length;

  // Only allow these roles to access the page
  const allowedRoles = ['admin', 'god', 'os_team'];

  if (!userRole) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Please log in to access Emergency Alerts.</Alert>
      </Container>
    );
  }

  if (!allowedRoles.includes(userRole)) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">You do not have permission to access Emergency Alerts.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)' }}>
        <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon sx={{ fontSize: 48, color: 'white', mr: 2 }} />
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
              Emergency Alerts
            </Typography>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Real-time emergency response management
                </Typography>
              </Box>
          </Box>
        </Grid>
        <Grid item>
            <Stack direction="row" spacing={2} alignItems="center">
              {/* Connection Status Indicator */}
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'white' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: connectionStatus === 'connected' ? '#4caf50' : 
                             connectionStatus === 'reconnecting' ? '#ff9800' : '#f44336',
                    mr: 1,
                    animation: connectionStatus === 'reconnecting' ? 'pulse 1s infinite' : 'none',
                  }}
                />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  {connectionStatus === 'connected' ? 'Live' : 
                   connectionStatus === 'reconnecting' ? 'Reconnecting...' : 'Offline'}
                </Typography>
              </Box>
          <Button
            variant="contained"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
                onClick={fetchAlerts}
          >
                Refresh
          </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Connection Status Alert */}
      {connectionStatus === 'disconnected' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Connection lost. Attempting to reconnect...
          </Typography>
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #f44336' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <WarningIcon sx={{ color: '#f44336', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f44336' }}>
                {activeCount}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Active Alerts</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #ff9800' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <PendingIcon sx={{ color: '#ff9800', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                {respondedCount}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Responded</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #4caf50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                {resolvedCount}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Resolved</Typography>
          </Paper>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search alerts by username, group, notes, or location..."
          value={searchQuery}
          onChange={(_e) => setSearchQuery(_e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchQuery && (
              <IconButton onClick={() => setSearchQuery('')} size="small">
                <ClearIcon />
              </IconButton>
            ),
          }}
          size="small"
        />
      </Paper>

      {/* Tabs and Controls */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ px: 2 }}
          >
            <Tab 
              label={
                <Badge badgeContent={activeCount} color="error">
                  <span>Active</span>
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={respondedCount} color="warning">
                  <span>Responded</span>
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={resolvedCount} color="success">
                  <span>Resolved</span>
                </Badge>
              } 
            />
          </Tabs>
        </Box>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {sortedAlerts.length} alert{sortedAlerts.length !== 1 ? 's' : ''} found
            {searchQuery && ` matching "${searchQuery}"`}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value as 'created_at' | 'status')}
            >
              <MenuItem value="created_at">Date Created</MenuItem>
              <MenuItem value="status">Status</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Alerts Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : sortedAlerts.length === 0 ? (
        <Paper elevation={2} sx={{ p: 8, textAlign: 'center' }}>
          <WarningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No emergency alerts found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {activeTab === 0 ? 'No active alerts at this time.' : 
             activeTab === 1 ? 'No responded alerts.' : 'No resolved alerts.'}
            {searchQuery && ` No alerts match "${searchQuery}".`}
          </Typography>
          {(searchQuery || activeTab !== 0) && (
            <Button
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setActiveTab(0);
              }}
              sx={{ mt: 2 }}
            >
              Clear Filters
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {sortedAlerts.map((alert, index) => (
            <Grid item xs={12} md={6} lg={4} key={alert.id}>
              <Fade in timeout={300 + (index * 100)}>
              <Card 
                  elevation={alert.status === 'active' ? 8 : 2}
                sx={{ 
                  height: '100%',
                  border: alert.status === 'active' ? '2px solid #f44336' : '1px solid #e0e0e0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: alert.status === 'active' ? 
                        '0 8px 25px rgba(244, 67, 54, 0.4)' : 
                        '0 8px 25px rgba(0,0,0,0.15)',
                    },
                    position: 'relative',
                    overflow: 'visible',
                }}
              >
                  {alert.status === 'active' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        width: 8,
                        height: 'calc(100% + 4px)',
                        bgcolor: '#f44336',
                        borderRadius: '4px 0 0 4px',
                        animation: 'pulse 2s infinite',
                      }}
                    />
                  )}
                  
                  <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: alert.status === 'active' ? '#f44336' : '#757575', mr: 2 }}>
                          {alert.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {alert.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {alert.user_group}
                          </Typography>
                          {alert.hp_number && (
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                              <PhoneIcon sx={{ fontSize: 16, mr: 0.5, mb: '-2px' }} />
                              <a href={`tel:${alert.hp_number}`} style={{ color: '#1976d2', textDecoration: 'none' }}>{alert.hp_number}</a>
                            </Typography>
                          )}
                        </Box>
                    </Box>
                    <Chip 
                        label={getStatusText(alert.status)} 
                      color={getStatusColor(alert.status)} 
                      size="small" 
                        sx={{ fontWeight: 600 }}
                    />
                  </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Location */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {alert.address || 'Unknown location'}
                  </Typography>
                  </Box>

                    {/* Time */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimeIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(alert.created_at)}
                    </Typography>
                  </Box>

                    {/* Notes */}
                  {alert.notes && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ 
                          fontStyle: 'italic', 
                          bgcolor: 'grey.50', 
                          p: 1.5, 
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}>
                      <strong>Notes:</strong> {alert.notes}
                    </Typography>
                      </Box>
                  )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      <Tooltip title="View Details">
                    <Button
                      variant="outlined"
                      size="small"
                          startIcon={<VisibilityIcon />}
                      onClick={() => openUpdateDialog(alert)}
                          sx={{ flex: 1 }}
                        >
                          Details
                        </Button>
                      </Tooltip>
                      {alert.status === 'active' && (
                        <Tooltip title="Respond to Alert">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PhoneIcon />}
                            onClick={() => openRespondDialog(alert)}
                            color="warning"
                          >
                            Respond
                          </Button>
                        </Tooltip>
                      )}
                      {alert.status === 'responded' && (
                        <Tooltip title="Resolve Alert">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => openResolveDialog(alert)}
                            color="success"
                          >
                            Resolve
                    </Button>
                        </Tooltip>
                      )}
                      <Tooltip title="View on Map">
                    <Button
                      variant="outlined"
                      size="small"
                          startIcon={<MapIcon />}
                      onClick={() => {
                        window.open(
                          `https://www.google.com/maps?q=${alert.latitude},${alert.longitude}`,
                          '_blank'
                        );
                      }}
                    >
                          Map
                    </Button>
                      </Tooltip>
                  </Box>
                </CardContent>
              </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Enhanced Update Status Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon sx={{ mr: 1 }} />
            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Emergency Alert Details</Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedAlert && (
            <Box>
              <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Alert Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>User:</strong> {selectedAlert.username}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Group:</strong> {selectedAlert.user_group}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Location:</strong> {selectedAlert.address || 'Unknown location'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Coordinates:</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {selectedAlert.latitude.toFixed(6)}, {selectedAlert.longitude.toFixed(6)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Created:</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      {formatDateTime(selectedAlert.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Current Status:</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Chip 
                      label={getStatusText(selectedAlert.status)} 
                      color={getStatusColor(selectedAlert.status)} 
                      size="small"
                    />
                  </Grid>
                  {selectedAlert.notes && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Notes:</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ 
                          fontStyle: 'italic', 
                          bgcolor: 'white', 
                          p: 1.5, 
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}>
                          {selectedAlert.notes}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            variant="contained"
            sx={{ minWidth: 120 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Respond Dialog */}
      <Dialog 
        open={respondDialogOpen} 
        onClose={() => setRespondDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
      >
        <DialogTitle sx={{ 
          bgcolor: 'warning.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ mr: 1 }} />
            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Respond to Emergency</Typography>
          </Box>
          <IconButton onClick={() => setRespondDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedAlert && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You are responding to an emergency alert from <strong>{selectedAlert.username}</strong>.
              </Typography>
              {selectedAlert.hp_number && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Call User:</strong>{' '}
                  <a href={`tel:${selectedAlert.hp_number}`} style={{ color: '#1976d2', fontWeight: 600 }}>
                    {selectedAlert.hp_number}
                  </a>
              </Typography>
              )}
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Response Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe your response..."
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setRespondDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRespond}
            variant="contained"
            color="warning"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : <PhoneIcon />}
            sx={{ minWidth: 120 }}
          >
            {updating ? 'Responding...' : 'Respond'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog 
        open={resolveDialogOpen} 
        onClose={() => setResolveDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
      >
        <DialogTitle sx={{ 
          bgcolor: 'success.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 1 }} />
            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Resolve Emergency</Typography>
          </Box>
          <IconButton onClick={() => setResolveDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedAlert && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You are resolving an emergency alert from <strong>{selectedAlert.username}</strong>.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Resolution Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe how the emergency was resolved..."
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setResolveDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleResolve}
            variant="contained"
            color="success"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{ minWidth: 120 }}
          >
            {updating ? 'Resolving...' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
      `}</style>
    </Container>
  );
} 